import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import EmojiPicker, { Emoji, EmojiStyle, Theme } from "emoji-picker-react";
import "./themeupdate.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import { getAdminQustions } from "../../functions/getAdminQuestions";
import { saveAdminQuestions } from "../../functions/saveAdminQuestions";
import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import { countGraphemes, graphemeToUnified, splitGraphemes } from "../../../utils/grapheme";

const MAX_QUESTION_ANSWER_LEN = 50;
const MAX_QUESTION_HINT_LEN = 150;
const MAX_QUESTIONS = 20;
const MAX_EMOJI_SLOTS = 7;
/** Slightly compact panel — full default size felt “zoomed” on admin layout */
const EMOJI_PICKER_WIDTH = 280;
const EMOJI_PICKER_HEIGHT = 380;

const looksLikeImageKey = (s) => /\.(png|jpe?g|gif|webp)$/i.test(String(s || "").trim());

/** Viewport position for fixed picker; keeps full panel on-screen. */
function computeEmojiPickerPosition(anchorEl) {
  const rect = anchorEl.getBoundingClientRect();
  const pad = 8;
  let left = rect.left;
  let top = rect.bottom + 8;
  if (left + EMOJI_PICKER_WIDTH > window.innerWidth - pad) {
    left = Math.max(pad, window.innerWidth - EMOJI_PICKER_WIDTH - pad);
  }
  if (left < pad) left = pad;
  if (top + EMOJI_PICKER_HEIGHT > window.innerHeight - pad) {
    top = Math.max(pad, rect.top - EMOJI_PICKER_HEIGHT - 8);
  }
  if (top < pad) top = pad;
  return { top, left };
}

const emptySlot = () => ({ unified: "", emoji: "" });

const emptyQuestion = () => ({
  id: null,
  answer: "",
  hint: "",
  emojiSlots: [emptySlot()],
  persistImageName: "",
});

/** Normalize legacy string slots or picker objects so rendering always has unified + character. */
function normalizeEmojiSlot(s) {
  if (s && typeof s === "object" && ("emoji" in s || "unified" in s)) {
    const emoji = String(s.emoji ?? "");
    let unified = String(s.unified ?? "").toLowerCase();
    if (!unified && emoji) unified = graphemeToUnified(emoji);
    return { unified, emoji };
  }
  if (typeof s === "string" && s) {
    return { unified: graphemeToUnified(s), emoji: s };
  }
  return emptySlot();
}

function mapApiRowToQuestionState(q) {
  const id = q.id ?? null;
  const answer = String(q.answer ?? q.correct_answer ?? q.correctAnswer ?? "").trim();
  const hint = String(q.hint ?? "").trim();
  const img = String(q.image ?? "").trim();
  const qu = String(q.question ?? "").trim();

  if (img && looksLikeImageKey(img)) {
    return {
      id,
      answer,
      hint,
      emojiSlots: [emptySlot()],
      persistImageName: img,
    };
  }

  if (qu && !looksLikeImageKey(qu)) {
    const glyphs = splitGraphemes(qu).slice(0, MAX_EMOJI_SLOTS);
    return {
      id,
      answer,
      hint,
      emojiSlots: glyphs.length
        ? glyphs.map((g) => ({ unified: graphemeToUnified(g), emoji: g }))
        : [emptySlot()],
      persistImageName: "",
    };
  }

  if (qu && looksLikeImageKey(qu)) {
    return {
      id,
      answer,
      hint,
      emojiSlots: [emptySlot()],
      persistImageName: qu,
    };
  }

  return { id, answer, hint, emojiSlots: [emptySlot()], persistImageName: "" };
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);

  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [emojiPickerTarget, setEmojiPickerTarget] = useState(null);
  const [emojiPickerPos, setEmojiPickerPos] = useState(null);
  const pickerWrapRef = useRef(null);
  const emojiPickerAnchorRef = useRef(null);

  const closeEmojiPicker = useCallback(() => {
    setEmojiPickerTarget(null);
    setEmojiPickerPos(null);
    emojiPickerAnchorRef.current = null;
  }, []);

  const requiredTotalQuestions = useMemo(() => {
    const n = parseInt(String(themeData?.total_question ?? ""), 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return Math.min(MAX_QUESTIONS, n);
  }, [themeData?.total_question]);

  useEffect(() => {
    if (currentTheme && adminToken) {
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    }
  }, [dispatch, currentTheme, adminToken]);

  useEffect(() => {
    if (!emojiPickerTarget) return;
    const onDown = (e) => {
      if (pickerWrapRef.current?.contains(e.target)) return;
      closeEmojiPicker();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeEmojiPicker();
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [emojiPickerTarget, closeEmojiPicker]);

  /** Keep picker aligned with the slot while the page scrolls (do not close on scroll). */
  useEffect(() => {
    if (!emojiPickerTarget) return;
    const anchor = emojiPickerAnchorRef.current;
    if (!anchor) return;
    const reposition = () => {
      const a = emojiPickerAnchorRef.current;
      if (!a) return;
      setEmojiPickerPos(computeEmojiPickerPosition(a));
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [emojiPickerTarget]);

  const questionEmojiJoined = (q) =>
    (q.emojiSlots || []).map(normalizeEmojiSlot).map((s) => s.emoji).filter(Boolean).join("");

  const persistQuestions = async (nextQuestions, { showSuccess = false } = {}) => {
    const trimmed = (nextQuestions || []).map((q) => ({
      ...q,
      answer: String(q.answer ?? "").trim(),
      hint: String(q.hint ?? "").trim(),
      emojiSlots: (q.emojiSlots || []).map(normalizeEmojiSlot),
    }));

    const joinedOf = (q) => questionEmojiJoined(q);
    const isBlankQuestionRow = (q) => {
      const j = joinedOf(q);
      return (
        countGraphemes(j) < 1 &&
        !String(q.persistImageName || "").trim() &&
        !q.answer &&
        !String(q.hint || "").trim()
      );
    };
    const isCompleteQuestionRow = (q) => {
      const j = joinedOf(q);
      const hasEmojiOrLegacy =
        countGraphemes(j) >= 1 || Boolean(String(q.persistImageName || "").trim());
      return hasEmojiOrLegacy && Boolean(q.answer) && Boolean(q.hint);
    };

    const partialIdx = trimmed.findIndex((q) => !isBlankQuestionRow(q) && !isCompleteQuestionRow(q));
    if (partialIdx !== -1) {
      const qNo = partialIdx + 1;
      const q = trimmed[partialIdx];
      const j = joinedOf(q);
      const legacy = String(q.persistImageName || "").trim();
      if (!q.answer) {
        await Swal.fire("Validation", `Question ${qNo}: please enter the correct answer.`, "warning");
        return;
      }
      if (countGraphemes(j) < 1 && !legacy) {
        await Swal.fire(
          "Validation",
          `Question ${qNo}: add at least one emoji (tap a slot to open the emoji keyboard) before saving.`,
          "warning"
        );
        return;
      }
      if (!q.hint) {
        await Swal.fire("Validation", `Question ${qNo}: hint is required. Please enter a hint.`, "warning");
        return;
      }
      return;
    }

    const completeRows = trimmed.filter(isCompleteQuestionRow);
    if (completeRows.length < 1) {
      await Swal.fire(
        "Validation",
        "Add at least one complete question: emojis (or image), correct answer, and hint.",
        "warning"
      );
      return;
    }
    if (requiredTotalQuestions != null && completeRows.length < requiredTotalQuestions) {
      const diff = requiredTotalQuestions - completeRows.length;
      Swal.fire(
        "More questions needed",
        `Please add ${diff} more complete question${diff === 1 ? "" : "s"} to reach ${requiredTotalQuestions}.\n\nRequired: ${requiredTotalQuestions}\nComplete: ${completeRows.length}`,
        "warning"
      );
      return;
    }
    if (completeRows.length > MAX_QUESTIONS) {
      Swal.fire("Error", `Maximum ${MAX_QUESTIONS} questions.`, "error");
      return;
    }

    for (let i = 0; i < completeRows.length; i++) {
      const qNo = i + 1;
      const q = completeRows[i];
      const joined = questionEmojiJoined(q);
      const gCount = countGraphemes(joined);
      const legacy = String(q.persistImageName || "").trim();

      if (gCount > MAX_EMOJI_SLOTS) {
        Swal.fire("Validation", `Question ${qNo}: at most ${MAX_EMOJI_SLOTS} emojis.`, "warning");
        return;
      }
      if (gCount < 1 && !legacy) {
        Swal.fire("Validation", `Question ${qNo}: add at least one emoji.`, "warning");
        return;
      }
      if (!q.answer) {
        Swal.fire("Validation", `Question ${qNo}: please enter the correct answer.`, "warning");
        return;
      }
      if (q.answer.length > MAX_QUESTION_ANSWER_LEN) {
        Swal.fire("Validation", `Question ${qNo}: answer max ${MAX_QUESTION_ANSWER_LEN} characters.`, "warning");
        return;
      }
      if (!q.hint) {
        Swal.fire("Validation", `Question ${qNo}: hint is required.`, "warning");
        return;
      }
      if (q.hint.length > MAX_QUESTION_HINT_LEN) {
        Swal.fire("Validation", `Question ${qNo}: hint max ${MAX_QUESTION_HINT_LEN} characters.`, "warning");
        return;
      }
      const slotsNorm = (q.emojiSlots || []).map(normalizeEmojiSlot);
      if (gCount >= 1 && !legacy) {
        if (!slotsNorm.every((s) => Boolean(s.emoji))) {
          Swal.fire(
            "Validation",
            `Question ${qNo}: fill every emoji box, or remove extra empty slots before saving.`,
            "warning"
          );
          return;
        }
      }
    }

    const payloadQuestions = completeRows.map((q, idx) => {
      const joined = questionEmojiJoined(q);
      const hasEmoji = countGraphemes(joined) >= 1;
      const base = {
        id: q.id,
        sort_order: idx + 1,
        answer: q.answer,
        hint: q.hint,
      };
      if (hasEmoji) {
        return { ...base, imageName: "", questionEmoji: joined };
      }
      return { ...base, imageName: String(q.persistImageName || "").trim() };
    });

    setQuestionsLoading(true);
    try {
      await saveAdminQuestions({
        currentTheme,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        token: adminToken,
        questions: payloadQuestions,
        filesByIndex: {},
      });
      if (showSuccess) {
        await Swal.fire("Success", "Questions saved", "success");
      }
      await reloadQuestions();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save questions", "error");
    } finally {
      setQuestionsLoading(false);
    }
  };

  const reloadQuestions = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setQuestionsLoading(true);
    try {
      const res = await getAdminQustions({
        currentTheme,
        token: adminToken,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
      });
      const qs = Array.isArray(res?.questions) ? res.questions : Array.isArray(res?.allQuestions) ? res.allQuestions : [];
      if (qs.length) {
        const next = qs
          .sort((a, b) => (Number(a.sort_order ?? a.sortOrder ?? 0) || 0) - (Number(b.sort_order ?? b.sortOrder ?? 0) || 0))
          .map(mapApiRowToQuestionState);
        setQuestions(next);

        if (requiredTotalQuestions != null && next.length < requiredTotalQuestions) {
          const diff = requiredTotalQuestions - next.length;
          Swal.fire(
            "Total questions required",
            `This theme requires ${requiredTotalQuestions} questions. Please add ${diff} more.`,
            "info"
          );
        }
      } else {
        setQuestions([emptyQuestion()]);
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to fetch questions", "error");
    } finally {
      setQuestionsLoading(false);
    }
  }, [adminToken, currentTheme, organizationId, sessionId, requiredTotalQuestions]);

  useEffect(() => {
    reloadQuestions();
  }, [reloadQuestions]);

  const addQuestionRow = () => {
    if (questions.length >= MAX_QUESTIONS) {
      Swal.fire("Limit", `Maximum ${MAX_QUESTIONS} questions.`, "warning");
      return;
    }
    setQuestions((p) => [...p, emptyQuestion()]);
  };

  const addEmojiSlot = (qIdx) => {
    setQuestions((p) =>
      p.map((row, i) => {
        if (i !== qIdx) return row;
        if (row.emojiSlots.length >= MAX_EMOJI_SLOTS) return row;
        return { ...row, emojiSlots: [...row.emojiSlots, emptySlot()] };
      })
    );
  };

  const removeEmojiSlot = (qIdx, slotIdx) => {
    setQuestions((p) =>
      p.map((row, i) => {
        if (i !== qIdx) return row;
        if (row.emojiSlots.length <= 1) return row;
        const next = row.emojiSlots.filter((_, j) => j !== slotIdx);
        return { ...row, emojiSlots: next.length ? next : [emptySlot()] };
      })
    );
  };

  const setSlotFromPicker = (qIdx, slotIdx, emojiData) => {
    const unifiedRaw = String(emojiData?.unified ?? "")
      .toLowerCase()
      .replace(/\s+/g, "");
    const emojiChar = String(emojiData?.emoji ?? "");
    const unified = unifiedRaw || (emojiChar ? graphemeToUnified(emojiChar) : "");
    setQuestions((p) =>
      p.map((row, i) => {
        if (i !== qIdx) return row;
        const slots = [...row.emojiSlots.map(normalizeEmojiSlot)];
        slots[slotIdx] = { unified, emoji: emojiChar };
        return { ...row, emojiSlots: slots, persistImageName: emojiChar ? "" : row.persistImageName };
      })
    );
  };

  const updateQuestions = async () => {
    await persistQuestions(questions, { showSuccess: true });
  };

  const onEmojiPicked = (emojiData) => {
    if (!emojiPickerTarget) return;
    const { qIdx, slotIdx } = emojiPickerTarget;
    setSlotFromPicker(qIdx, slotIdx, emojiData);
    closeEmojiPicker();
  };

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="themeupdate-panel-wide">
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">Game Questions</div>
          <div className="themeupdate-card-body">
            <p className="themeupdate-muted">
              <ul>
                <li>
                  <code>Hint: required, max {MAX_QUESTION_HINT_LEN} characters</code>
                </li>
                <li>
                  <code>Question: up to {MAX_EMOJI_SLOTS} emojis (tap a slot to open the emoji keyboard)</code>
                </li>
                <li>
                  <code>
                    Answer limit: {MAX_QUESTION_ANSWER_LEN} characters
                  </code>
                </li>
                <li>
                  <code>Max questions: {MAX_QUESTIONS}</code>
                </li>
                <li>
                  <code>Min questions: 1</code>
                </li>
              </ul>
            </p>

            {questionsLoading ? (
              <div className="themeupdate-loading">Loading...</div>
            ) : (
              <div className="themeupdate-questions-grid">
                {questions.map((q, idx) => {
                  const qNo = idx + 1;
                  const joinedPreview = questionEmojiJoined(q);
                  const legacyUrl = q.persistImageName ? getDvSourceImageUrl(q.persistImageName) : "";
                  const showLegacyImg = Boolean(legacyUrl) && countGraphemes(joinedPreview) < 1;

                  return (
                    <div key={idx} className="themeupdate-question-card">
                      <div className="themeupdate-question-head">
                        <div className="themeupdate-question-title">Question {qNo}</div>
                        <button
                          type="button"
                          className="themeupdate-question-remove"
                          title="Delete this question"
                          disabled={questionsLoading}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const next = questions.filter((_, i) => i !== idx);
                            if (!next.length) {
                              setQuestions([emptyQuestion()]);
                              Swal.fire("Error", "Minimum 1 question is required.", "error");
                              return;
                            }
                            setQuestions(next);
                            await persistQuestions(next, { showSuccess: false });
                          }}
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>

                      <div className="themeupdate-question-body">
                        <label className="themeupdate-label">Question emojis</label>
                        <div className="themeupdate-emoji-slots-row">
                          {q.emojiSlots.map((slot, slotIdx) => {
                            const s = normalizeEmojiSlot(slot);
                            return (
                            <div key={slotIdx} className="themeupdate-emoji-slot-wrap">
                              <button
                                type="button"
                                className={`themeupdate-emoji-slot${emojiPickerTarget?.qIdx === idx && emojiPickerTarget?.slotIdx === slotIdx ? " themeupdate-emoji-slot--active" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const el = e.currentTarget;
                                  emojiPickerAnchorRef.current = el;
                                  setEmojiPickerPos(computeEmojiPickerPosition(el));
                                  setEmojiPickerTarget({ qIdx: idx, slotIdx });
                                }}
                                title="Choose emoji"
                              >
                                {s.emoji ? (
                                  <span className="themeupdate-emoji-slot-img-wrap">
                                    <Emoji
                                      unified={s.unified || graphemeToUnified(s.emoji)}
                                      emojiStyle={EmojiStyle.APPLE}
                                      size={30}
                                    />
                                  </span>
                                ) : (
                                  <i className="fa-solid fa-icons themeupdate-emoji-slot-placeholder-icon" aria-hidden />
                                )}
                              </button>
                              {q.emojiSlots.length > 1 ? (
                                <button
                                  type="button"
                                  className="themeupdate-emoji-slot-remove"
                                  title="Remove slot"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeEmojiSlot(idx, slotIdx);
                                  }}
                                >
                                  <i className="fa-solid fa-xmark" aria-hidden />
                                </button>
                              ) : null}
                            </div>
                            );
                          })}
                          {q.emojiSlots.length < MAX_EMOJI_SLOTS ? (
                            <button
                              type="button"
                              className="themeupdate-emoji-add-slot"
                              title="Add emoji slot"
                              onClick={() => addEmojiSlot(idx)}
                            >
                              <i className="fa-solid fa-plus" aria-hidden />
                            </button>
                          ) : null}
                        </div>

                        <div className="themeupdate-question-preview themeupdate-question-preview--emoji">
                          <div className="themeupdate-preview-label">Preview</div>
                          {countGraphemes(joinedPreview) >= 1 ? (
                            <div className="themeupdate-emoji-preview-row">
                              {q.emojiSlots
                                .map(normalizeEmojiSlot)
                                .filter((slot) => slot.emoji)
                                .map((slot, gi) => (
                                  <React.Fragment key={`${gi}-${slot.unified}-${slot.emoji}`}>
                                    {gi > 0 ? <span className="themeupdate-emoji-preview-plus">+</span> : null}
                                    <span className="themeupdate-emoji-preview-glyph-wrap">
                                      <Emoji
                                        unified={slot.unified || graphemeToUnified(slot.emoji)}
                                        emojiStyle={EmojiStyle.APPLE}
                                        size={38}
                                      />
                                    </span>
                                  </React.Fragment>
                                ))}
                            </div>
                          ) : showLegacyImg ? (
                            <img src={legacyUrl} alt="" className="themeupdate-question-preview-img" />
                          ) : (
                            <div className="themeupdate-emoji-preview-empty">Add emojis to preview</div>
                          )}
                        </div>

                        <label className="themeupdate-label">Correct answer</label>
                        <input
                          type="text"
                          className="themeupdate-input"
                          maxLength={MAX_QUESTION_ANSWER_LEN}
                          value={q.answer}
                          onChange={(e) =>
                            setQuestions((p) => p.map((x, i) => (i === idx ? { ...x, answer: e.target.value } : x)))
                          }
                        />
                        <div className="themeupdate-hint">
                          {String(q.answer || "").length}/{MAX_QUESTION_ANSWER_LEN}
                        </div>

                        <label className="themeupdate-label">Hint</label>
                        <textarea
                          className="themeupdate-input themeupdate-question-textarea"
                          maxLength={MAX_QUESTION_HINT_LEN}
                          rows={2}
                          placeholder="Required hint for players"
                          value={q.hint ?? ""}
                          onChange={(e) =>
                            setQuestions((p) =>
                              p.map((x, i) => (i === idx ? { ...x, hint: e.target.value } : x))
                            )
                          }
                        />
                        <div className="themeupdate-hint">
                          {String(q.hint || "").length}/{MAX_QUESTION_HINT_LEN}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {emojiPickerTarget && emojiPickerPos
              ? createPortal(
                  <div
                    ref={pickerWrapRef}
                    className="themeupdate-emoji-picker-portal"
                    style={{ top: emojiPickerPos.top, left: emojiPickerPos.left }}
                  >
                    <EmojiPicker
                      onEmojiClick={onEmojiPicked}
                      theme={Theme.LIGHT}
                      emojiStyle={EmojiStyle.APPLE}
                      width={EMOJI_PICKER_WIDTH}
                      height={EMOJI_PICKER_HEIGHT}
                      searchDisabled={false}
                      lazyLoadEmojis={false}
                      previewConfig={{
                        showPreview: true,
                        defaultCaption: "Pick an emoji",
                      }}
                    />
                  </div>,
                  document.body
                )
              : null}

            <div className="themeupdate-questions-actions">
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-success"
                onClick={updateQuestions}
                disabled={questionsLoading}
              >
                Save
              </button>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-outline"
                onClick={addQuestionRow}
                disabled={questionsLoading}
              >
                Add question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeUpdate;
